import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { Small } from 'foundations-components/transitional/components/Small';
import styled from 'foundations-theming/utils';
import { useId } from 'react';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TermOption = ({
  children,
  name,
  selected,
  'data-value': dataValue
}) => {
  const labelId = useId();
  return /*#__PURE__*/_jsxs(Flex, {
    inlineSize: "calc(50% - 10px)",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2,
    id: labelId,
    "aria-checked": selected,
    "data-value": dataValue,
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: /*#__PURE__*/_jsx(RadioInput, {
        checked: selected,
        "aria-checked": selected,
        "aria-labelledby": labelId,
        name: name
      })
    }), children]
  });
};
const PaymentLabel = ({
  selected,
  children
}) => {
  return /*#__PURE__*/_jsx(Text, {
    color: selected ? 'text-interactive-default' : 'text-core-default',
    typography: "heading-25",
    textAlign: "center",
    as: "div",
    children: children
  });
};
const SelectedTerm = styled.div.withConfig({
  displayName: "BaseTermSelectorComponent__SelectedTerm",
  componentId: "sc-1mpj9ap-0"
})(({
  theme,
  activeTerm
}) => {
  return {
    position: 'absolute',
    insetBlockStart: '-3px',
    insetInlineStart: activeTerm === MONTHLY ? '-1px' : 'calc(50% - 3px)',
    blockSize: 'calc(100% + 6px)',
    inlineSize: 'calc(50% + 4px)',
    background: theme.color['fill-surface-default'],
    borderRadius: theme.borderRadius['full'],
    borderWidth: theme.borderWidth[100],
    borderStyle: 'solid',
    borderColor: theme.color['border-interactive-pressed'],
    transition: 'all 0.2s ease-in-out'
  };
});
export const getDiscountPercentageToShow = (discount1, discount2) => {
  const relativeDiscount = Math.abs(100 - discount1 - (100 - discount2)) / (100 - Math.min(discount1, discount2)) * 100;
  return relativeDiscount === 10 ? relativeDiscount : null;
};
export const BaseTermSelectorComponent = ({
  onToggleChange,
  trackingSource,
  activeTerm,
  availableTerms,
  hasAnnualDiscount
}) => {
  const radioGroupId = useId();
  return /*#__PURE__*/_jsx(View, {
    cursor: "pointer",
    backgroundColor: "fill-tertiary-default",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-subtle",
    inlineSize: "100%",
    position: "relative",
    paddingBlock: "100",
    paddingInline: "300",
    borderRadius: "full",
    onClick: onToggleChange,
    "data-selenium-id": `term-selector-${trackingSource}`,
    children: /*#__PURE__*/_jsxs(Flex, {
      as: "fieldset",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsxs(TermOption, {
        selected: activeTerm === MONTHLY,
        name: radioGroupId,
        "data-value": MONTHLY,
        children: [/*#__PURE__*/_jsx(PaymentLabel, {
          selected: activeTerm === MONTHLY,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.detailedTermSelector.payMonthly"
          })
        }), /*#__PURE__*/_jsx(Small, {
          textAlign: "center",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: availableTerms[0].commitment === ANNUAL ? 'pricingPage.detailedTermSelector.commitAnnually' : 'pricingPage.detailedTermSelector.commitMonthly'
          })
        })]
      }), /*#__PURE__*/_jsxs(TermOption, {
        selected: activeTerm === ANNUAL,
        name: radioGroupId,
        "data-value": ANNUAL,
        children: [/*#__PURE__*/_jsxs(PaymentLabel, {
          selected: activeTerm === ANNUAL,
          children: [/*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: availableTerms[1].payment === ANNUAL ? 'pricingPage.detailedTermSelector.payUpfront' : 'pricingPage.detailedTermSelector.payMonthly'
            })
          }), hasAnnualDiscount ? /*#__PURE__*/_jsx(Text, {
            as: "span",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            color: "fill-positive-default",
            typography: "heading-25",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.detailedTermSelector.bestValue"
            })
          }) : null]
        }), /*#__PURE__*/_jsx(Text, {
          typography: "detail-100",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.detailedTermSelector.commitAnnually"
          })
        })]
      }), /*#__PURE__*/_jsx(SelectedTerm, {
        activeTerm: activeTerm
      })]
    })
  });
};